// ============================================================
// Der einzige Weg zum Backend (SPEC §2.1, §13.4).
// Kopie aus /opt/lilasprojekte/frontend/src/api/client.ts; geändert: der Kopf heißt
// X-KT, `ApiFehler.istKonflikt` liest `body` als die jeweilige Zeile (Karte, Kapitel,
// Katalog), und `api.upload` läuft über XMLHttpRequest, weil `fetch` keinen
// Upload-Fortschritt kennt (IFC bis 1 GiB, §8.1).
//
// Fünf Festlegungen stecken darin, und alle fünf haben einen Grund:
//
// 1. RELATIVE Adresse unter /api. Traefik führt /api auf das Backend und
//    schneidet das Präfix ab; bei `pnpm dev` tut dasselbe der Proxy in
//    vite.config.ts. Es gibt bewusst kein VITE_API_URL.
// 2. cache: "no-store". Ohne die Zeile wendet der Browser auf Antworten ohne
//    Cache-Control eine GESCHÄTZTE Haltbarkeit an.
// 3. credentials: "same-origin". Der Sitzungskeks `__Host-kt_sitzung` ist
//    SameSite und HttpOnly; JavaScript sieht ihn nie, er muss aber mit.
// 4. Kopfzeile `X-KT: 1` auf jeder VERÄNDERNDEN Anfrage (§1.2). Das ist der
//    CSRF-Riegel des Hauses: ein fremdes Formular kann eigene Kopfzeilen nicht
//    setzen, ohne einen Vorflug auszulösen — und der scheitert, weil das Backend
//    kein CORS spricht. Der Riegel hängt an der METHODE, nicht am Aufrufer.
// 5. Ein 401 setzt den Anmeldespeicher zurück — über einen ANGEMELDETEN Rückruf,
//    nicht über einen Import (`store/useAuthStore.ts` importiert diese Datei; ein
//    Import zurück wäre ein Kreis).
// ============================================================
import type { Fehler } from "./types";

const BASIS = "/api";

/** Methoden, die etwas ändern — sie bekommen den CSRF-Riegel. */
const VERAENDERND = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** Der CSRF-Kopf (§1.2). Steht EINMAL hier. */
export const CSRF_KOPF = "X-KT";

export class ApiFehler extends Error {
  /** HTTP-Status; 0 bedeutet: die Anfrage kam gar nicht erst an (Netz, Abbruch). */
  readonly status: number;
  /** Der deutsche Satz aus `{"detail": …}` — oder unser Ersatz. Gleich `message`. */
  readonly detail: string;
  /** Der ausgewertete Antwortkörper. Bei 409 steht darin die aktuelle Zeile + `detail`. */
  readonly body: unknown;
  /** Die aufgerufene Adresse, damit eine Meldung ohne Zusatzarbeit brauchbar ist. */
  readonly url: string;

  constructor(status: number, detail: string, body: unknown, url: string) {
    super(detail);
    this.name = "ApiFehler";
    this.status = status;
    this.detail = detail;
    this.body = body;
    this.url = url;
  }

  /** true bei „nicht angemeldet". */
  get istAbgemeldet(): boolean {
    return this.status === 401;
  }

  /** true bei „inzwischen geändert" (E6) — dann trägt `body` die aktuelle Zeile. */
  get istKonflikt(): boolean {
    return this.status === 409;
  }

  /**
   * Die aktuelle Zeile aus einem 409-Rumpf, OHNE `detail` (§15.4: „der Client entfernt
   * `detail` vor dem Zwischenspeichern"). `null`, wenn es kein Konflikt mit Zeile ist —
   * etwa „Mindestens ein Admin muss bleiben." (auch 409, aber ohne Zeile).
   */
  aktuelleZeile<T>(): T | null {
    if (!this.istKonflikt || !this.body || typeof this.body !== "object") return null;
    const { detail: _weg, ...zeile } = this.body as Record<string, unknown>;
    void _weg;
    return "version" in zeile ? (zeile as T) : null;
  }
}

/* --------------------------------------------------------- 401-Registrierung */

let beiAbmeldung: (() => void) | null = null;

/**
 * Wird EINMAL von `store/useAuthStore.ts` beim Laden des Moduls gerufen. Danach
 * setzt jeder 401 den Anmeldestatus auf „anonym", und der Wächter in App.tsx führt
 * zur Anmeldung — ohne Toast: ein abgelaufener Keks ist kein Fehler.
 */
export function setzeAbmeldeHandler(fn: () => void): void {
  beiAbmeldung = fn;
}

/* ------------------------------------------------------------------ Meldungen */

const NICHT_ERREICHBAR = "Server nicht erreichbar — bitte neu laden.";

/** Deutsche Standardsätze für die Fälle, in denen der Server keinen mitschickt. */
export function standardMeldung(status: number): string {
  if (status === 0) return "Keine Verbindung zum Server. Ist das Netz noch da?";
  if (status === 400) return "Die Eingabe hat der Server nicht angenommen.";
  if (status === 401) return "Nicht (mehr) angemeldet.";
  if (status === 403) return "Dazu fehlt die Berechtigung.";
  if (status === 404) return "Das gibt es nicht (mehr).";
  if (status === 409) return "Das wurde zwischenzeitlich geändert.";
  if (status === 413) return "Die Datei ist zu groß.";
  if (status === 415) return "Dieses Dateiformat nehmen wir nicht an.";
  if (status === 422) return "Die Eingabe passt nicht.";
  if (status === 429) return "Zu viele Versuche. Bitte kurz warten.";
  if (status === 503) return "Der Server ist gerade nicht erreichbar. Gleich noch einmal versuchen.";
  if (status >= 500) return "Der Server hat einen Fehler gemeldet.";
  return `Die Anfrage ist fehlgeschlagen (${status}).`;
}

/**
 * Meldung aus dem Antwortkörper ziehen. FastAPI schreibt `detail`, unser nginx-503
 * ebenfalls. Ist `detail` eine Liste (FastAPIs Prüffehler), wird daraus eine Zeile.
 */
export function meldungAus(body: unknown, status: number): string {
  if (typeof body === "string" && body.trim() && !body.trim().startsWith("<")) return body.trim();
  if (body && typeof body === "object") {
    const o = body as Record<string, unknown>;
    if (typeof o.detail === "string" && o.detail.trim()) return o.detail.trim();
    if (Array.isArray(o.detail)) {
      const teile = o.detail
        .map((d) => (d && typeof d === "object" && typeof (d as Record<string, unknown>).msg === "string" ? (d as Record<string, string>).msg : null))
        .filter(Boolean);
      if (teile.length) return teile.join(" · ");
    }
  }
  return standardMeldung(status);
}

/** Körper auswerten, ohne an einer leeren oder nicht-JSON-Antwort zu zerbrechen. */
function auswerten(roh: string, typ: string): unknown {
  if (!roh) return null;
  if (typ.includes("json")) {
    try {
      return JSON.parse(roh);
    } catch {
      return roh;
    }
  }
  return roh;
}

export function adresse(pfad: string): string {
  if (pfad.startsWith("http://") || pfad.startsWith("https://")) return pfad;
  return BASIS + (pfad.startsWith("/") ? pfad : "/" + pfad);
}

export interface AnfrageOpts extends Omit<RequestInit, "body"> {
  /** wird als JSON gesendet; für FormData bitte `api.upload()` nehmen */
  json?: unknown;
  /** roher Körper (FormData, Blob, string) — dann setzt diese Funktion KEIN content-type */
  body?: BodyInit | null;
}

/**
 * Eine Anfrage ans Backend. Gibt den ausgewerteten Körper zurück; bei 204 oder
 * leerem Körper `undefined`. Wirft bei jedem Status ≥ 400 und bei Netzfehlern einen
 * `ApiFehler` mit einer deutschen `detail`, die direkt in einen Toast passt.
 */
export async function anfrage<T = unknown>(pfad: string, opts: AnfrageOpts = {}): Promise<T> {
  const { json, body, headers, method, ...rest } = opts;
  const m = (method ?? (json !== undefined || body !== undefined ? "POST" : "GET")).toUpperCase();
  const h = new Headers(headers);
  h.set("Accept", "application/json");
  if (VERAENDERND.has(m)) h.set(CSRF_KOPF, "1");
  if (json !== undefined) h.set("Content-Type", "application/json");
  // Bei FormData setzt der Browser den content-type selbst — mit der Trennmarke,
  // die nur er kennt. Ein eigener Wert hier macht den Upload unlesbar.

  const url = adresse(pfad);
  let res: Response;
  try {
    res = await fetch(url, {
      ...rest,
      method: m,
      headers: h,
      cache: "no-store",
      credentials: "same-origin",
      body: json !== undefined ? JSON.stringify(json) : (body ?? null),
    });
  } catch (e) {
    // Ein Abbruch über AbortController ist KEIN Fehler der Anwendung.
    if (e instanceof DOMException && e.name === "AbortError") throw e;
    throw new ApiFehler(0, standardMeldung(0), null, url);
  }

  if (res.status === 204 || res.status === 205) return undefined as T;

  const koerper = auswerten(await res.text(), res.headers.get("content-type") ?? "");

  if (!res.ok) {
    // Der 401 zuerst, vor dem Werfen: sonst hängt es am Aufrufer, ob der
    // Anmeldestatus nachgeführt wird, und genau das wird irgendwo vergessen.
    if (res.status === 401 && beiAbmeldung) beiAbmeldung();
    throw new ApiFehler(res.status, meldungAus(koerper, res.status), koerper, url);
  }

  // Ein 200 OHNE JSON ist kein Erfolg, sondern ein Irrläufer: nginx liefert die
  // index.html aus, wenn /api ins Leere zeigt. Lieber ein ehrlicher Satz.
  if (typeof koerper === "string") throw new ApiFehler(res.status, NICHT_ERREICHBAR, koerper, url);
  return (koerper ?? undefined) as T;
}

/** Suchparameter bauen und dabei leere Werte weglassen. Eine Liste wird zu MEHREREN Parametern. */
export function frage(params: Record<string, string | number | boolean | string[] | null | undefined>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === "") continue;
    if (Array.isArray(v)) {
      for (const e of v) if (e !== "") p.append(k, e);
      continue;
    }
    p.set(k, String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}

export interface UploadOpts {
  /** Anteil 0…1 des HOCHGELADENEN Rumpfs — nicht der Verarbeitung danach. */
  onFortschritt?: (anteil: number, geladen: number, gesamt: number) => void;
  /** Abbrechen: `controller.abort()` bricht die Übertragung ab (AbortError). */
  signal?: AbortSignal;
  method?: "POST" | "PUT";
}

/**
 * Datei-Upload (multipart) über XMLHttpRequest — `fetch` kennt bis heute keinen
 * Upload-Fortschritt, und ein IFC-Upload von 400 MB ohne Balken ist eine Minute
 * Stille (§13.4). Setzt AUSDRÜCKLICH kein Content-Type: der Browser hängt die
 * Trennmarke selbst an. Dieselben Regeln wie `anfrage`: X-KT, 401-Riegel, `ApiFehler`.
 *
 *   const fd = new FormData();
 *   fd.append("datei", datei);
 *   await api.upload<Import>(`/projekte/${id}/importe`, fd, { onFortschritt: setAnteil });
 */
export function hochladen<T = unknown>(pfad: string, form: FormData, opts: UploadOpts = {}): Promise<T> {
  const url = adresse(pfad);
  return new Promise<T>((erfuellen, ablehnen) => {
    const xhr = new XMLHttpRequest();
    xhr.open(opts.method ?? "POST", url);
    xhr.withCredentials = false; // gleicher Ursprung: der Keks geht ohnehin mit
    xhr.setRequestHeader("Accept", "application/json");
    xhr.setRequestHeader(CSRF_KOPF, "1");
    if (opts.onFortschritt) {
      const melde = opts.onFortschritt;
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable && e.total > 0) melde(e.loaded / e.total, e.loaded, e.total);
      };
    }
    const abbrechen = () => xhr.abort();
    opts.signal?.addEventListener("abort", abbrechen, { once: true });
    xhr.onerror = () => ablehnen(new ApiFehler(0, standardMeldung(0), null, url));
    xhr.onabort = () => ablehnen(new DOMException("Abgebrochen", "AbortError"));
    xhr.onload = () => {
      opts.signal?.removeEventListener("abort", abbrechen);
      const koerper = auswerten(xhr.responseText, xhr.getResponseHeader("content-type") ?? "");
      if (xhr.status === 204 || xhr.status === 205) {
        erfuellen(undefined as T);
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        if (xhr.status === 401 && beiAbmeldung) beiAbmeldung();
        ablehnen(new ApiFehler(xhr.status, meldungAus(koerper, xhr.status), koerper, url));
        return;
      }
      if (typeof koerper === "string") {
        ablehnen(new ApiFehler(xhr.status, NICHT_ERREICHBAR, koerper, url));
        return;
      }
      erfuellen((koerper ?? undefined) as T);
    };
    xhr.send(form);
  });
}

export const api = {
  get: <T = unknown>(pfad: string, opts: AnfrageOpts = {}) => anfrage<T>(pfad, { ...opts, method: "GET" }),
  post: <T = unknown>(pfad: string, json?: unknown, opts: AnfrageOpts = {}) => anfrage<T>(pfad, { ...opts, method: "POST", json }),
  put: <T = unknown>(pfad: string, json?: unknown, opts: AnfrageOpts = {}) => anfrage<T>(pfad, { ...opts, method: "PUT", json }),
  patch: <T = unknown>(pfad: string, json?: unknown, opts: AnfrageOpts = {}) => anfrage<T>(pfad, { ...opts, method: "PATCH", json }),
  del: <T = void>(pfad: string, opts: AnfrageOpts = {}) => anfrage<T>(pfad, { ...opts, method: "DELETE" }),
  upload: <T = unknown>(pfad: string, form: FormData, opts: UploadOpts = {}) => hochladen<T>(pfad, form, opts),
};

/* ------------------------------------------------------------- Direktadressen
   Alles, was NICHT durch fetch läuft, sondern in ein <img src> oder ein <a href>
   geht. Der Strom läuft trotzdem durchs Backend und prüft die Sitzung — es gibt
   keine presigned URLs nach außen (T2). */

/** Ein Bild in einer Größe (§4.7, §4.11): `400.webp` (Raster), `1600.webp` (Panel),
 *  `original`. Der Server kennt genau diese drei Namen (`routers/bilder.py: GROESSEN`);
 *  `/bilder/<id>/400` ohne Endung war 404 (INTEGRATION P5-4, P7-8). */
export const bildAdresse = (bild_id: string, groesse: "400" | "1600" | "original" = "400") =>
  `${BASIS}/bilder/${bild_id}/${groesse === "original" ? "original" : `${groesse}.webp`}`;

/** Typwächter für `catch`-Zweige. */
export function istApiFehler(e: unknown): e is ApiFehler {
  return e instanceof ApiFehler;
}

/** `Fehler` ist die Form, die das Backend bei jedem 4xx schickt. */
export type Fehlerkoerper = Fehler;
